import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'universal-cookie';
import { useWindowSize } from 'usehooks-ts';

import IconClose from '@components/Images/Rebranding/IconClose';
import TIModal, { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { subscribe_path } from '@routes';
import { SHOW_MODAL_ON_INACTION_SECONDS } from '@src/env';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import { GlobalModuleType } from '@tracking';
import { hasShownOnboardingThisSession } from '@utils/backend-api/userOnboarding';
import { getClassByModalType } from '@utils/blueconic/utils';

import { MarketingModalProps } from '../index';
import MarketingCopy, { ModalType } from './MarketingCopy';

const cookies = new Cookies();

const updateWindowEvents = (value: () => void | null) => {
  window.onclick = value;
  window.onkeypress = value;
  window.ontouchstart = value;
  window.onmousemove = value;
  window.onmousedown = value;
  window.onscroll = value;
};

// Required for tests because window can't be resized to less than 500px
const MOBILE_BREAKPOINT = 500;
const SHOW_MODAL_AFTER_TIMES_CLOSED_SUBSCRIBER = 1;
const SHOW_MODAL_AFTER_TIMES_CLOSED_LEAD = 2;
const COOKIE_TIME_LIMIT_SECONDS = 60 * 60 * 24;

const getModalClosedCount = (cookies: Cookies, modalCookieName: string) => {
  const numTimesClosed = parseInt(cookies.get(modalCookieName));
  return isNaN(numTimesClosed) ? 0 : numTimesClosed;
};

const MarketingModalContent = ({
  analytics,
  articleSlugs,
  articleTitleSection,
  buttonText = 'Get started',
  customUrl,
  disclosure,
  highlight,
  id,
  imageURL,
  imgPathLarge,
  imgPathMedium,
  imgPathSmall,
  isActiveUser,
  leadType,
  mainCTA,
  mainURL,
  modalContainerClass,
  modalParentClass,
  modalTheme = TiModalColorScheme.dark,
  modalType,
  newsletterId,
  newsletterIds,
  newsletterImageUrl,
  onboardingMightShow,
  optionalCTA,
  optionalURL,
  proOfferCode,
  annualOfferCode,
  offerCode,
  offerLink = subscribe_path(),
  subtitle,
  title,
  externalSourceId,
}: MarketingModalProps) => {
  const [hasClosedModal, setHasClosedModal] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { width } = useWindowSize();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const closeIconRef = useRef<SVGSVGElement>(null);
  const userLeadType = externalSourceId ? `bc_${id}` : leadType;
  const { Track, trackEvent } = useEventTracking({
    ...analytics,
    creatorIds: null, // overwrite global props
    globalModuleId: id,
    globalModuleType: GlobalModuleType.Modal,
    location: TrackingLocation.Modal,
  });

  const showModalAfterTimesClosed = isActiveUser
    ? SHOW_MODAL_AFTER_TIMES_CLOSED_SUBSCRIBER
    : SHOW_MODAL_AFTER_TIMES_CLOSED_LEAD;
  const cookieExpiry = isActiveUser ? null : COOKIE_TIME_LIMIT_SECONDS;
  const MODAL_COOKIE_NAME = `marketing-modal-closed-${id}`;

  const canShowModal = useCallback(() => {
    const modalClosedCount = getModalClosedCount(cookies, MODAL_COOKIE_NAME);
    const preventShowModalFromCookies = modalClosedCount >= showModalAfterTimesClosed;

    if (onboardingMightShow && !hasShownOnboardingThisSession()) {
      return false;
    }

    return !preventShowModalFromCookies && !hasClosedModal && !isOpen;
  }, [showModalAfterTimesClosed, hasClosedModal, MODAL_COOKIE_NAME, isOpen, onboardingMightShow]);

  const showModal = useCallback(() => {
    if (canShowModal()) {
      clearTimeout(timerRef.current);
      setIsOpen(true);
      updateWindowEvents(null);
      trackEvent(impression({ impressionElement: ImpressionElement.Modal }));
    }
  }, [canShowModal, trackEvent]);

  const closeModal = () => {
    const modalClosedCount = getModalClosedCount(cookies, MODAL_COOKIE_NAME);
    const newValue = modalClosedCount ? modalClosedCount + 1 : 1;

    cookies.set(MODAL_COOKIE_NAME, newValue, { path: '/', maxAge: cookieExpiry });
    setIsOpen(false);
    setHasClosedModal(true);
    trackEvent(click({ clickTarget: ClickTarget.Close }));
  };

  const resetTime = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(showModal, SHOW_MODAL_ON_INACTION_SECONDS);
  }, [showModal]);

  // show modal when paywall is visible in viewport (for mobile only)
  useEffect(() => {
    const paywall = document.querySelector('[data-paywall]');

    const checkForPaywallVisibility = () => {
      const rect = paywall.getBoundingClientRect();
      if (rect.bottom <= window.innerHeight) {
        showModal();
      }
    };

    if (canShowModal() && paywall && width <= MOBILE_BREAKPOINT) {
      document.addEventListener('scroll', checkForPaywallVisibility);
    }

    return () => {
      document.removeEventListener('scroll', checkForPaywallVisibility);
    };
  }, [showModal, canShowModal, width]);

  useEffect(() => {
    if (canShowModal()) {
      resetTime();
      updateWindowEvents(resetTime);
      // show when mouse leaves page
      document.body.addEventListener('mouseleave', showModal);
    } else {
      document.body.removeEventListener('mouseleave', showModal);
    }

    return () => {
      document.body.removeEventListener('mouseleave', showModal);
    };
  }, [showModal, resetTime, canShowModal]);

  return (
    <Track>
      <TIModal
        isOpen={isOpen}
        onClose={closeModal}
        disableScrollLock={false}
        customHeader={true}
        isFullScreen={true}
        colorScheme={modalTheme}
        modalContainerClass={modalContainerClass}
        parentClass={clsx(
          modalParentClass,
          getClassByModalType(modalType),
          'w-full max-w-full xs:max-w-sm sm:max-w-xl md:max-w-5xl lg:max-w-container-lg',
          {
            'lg:!max-w-[1050px]': modalType === ModalType.LeadCaptureModalWithHorizontalArticles,
            'lg:!max-w-[1210px] !h-auto':
              modalType === ModalType.LeadCaptureWithSplitImg ||
              modalType === ModalType.LeadCaptureModalWithNewsletter ||
              modalType === ModalType.LeadCaptureModalWithMultipleSubscribeOptions,
          }
        )}
      >
        <div className="absolute right-8 top-8 flex justify-end sm:right-16 sm:top-16">
          <IconClose
            className={clsx('h-[26px] w-[26px] cursor-pointer', {
              'text-black': modalTheme === TiModalColorScheme.light,
              'text-white': modalTheme === TiModalColorScheme.dark,
            })}
            onClick={closeModal}
            ref={closeIconRef}
            data-test="close-modal"
            strokeWidth="1"
          />
        </div>
        <MarketingCopy
          annualOfferCode={annualOfferCode}
          articleSlugs={articleSlugs}
          articleTitleSection={articleTitleSection}
          buttonText={buttonText}
          closeIconRef={closeIconRef}
          customUrl={customUrl}
          disclosure={disclosure}
          highlight={highlight}
          imageURL={imageURL}
          imgPathLarge={imgPathLarge}
          imgPathMedium={imgPathMedium}
          imgPathSmall={imgPathSmall}
          leadType={userLeadType}
          mainCTA={mainCTA}
          mainURL={mainURL}
          modalId={id}
          modalType={modalType}
          modalTheme={modalTheme}
          newsletterId={newsletterId}
          newsletterIds={newsletterIds}
          newsletterImageUrl={newsletterImageUrl}
          offerCode={offerCode}
          offerLink={offerLink}
          onClose={closeModal}
          optionalCTA={optionalCTA}
          optionalURL={optionalURL}
          proOfferCode={proOfferCode}
          subtitle={subtitle}
          title={title}
        />
      </TIModal>
    </Track>
  );
};

export default MarketingModalContent;
